/**
 * 名寄せパターン設定パネルコンポーネント
 *
 * パターンの追加・表示・解除を管理
 */

import { useState } from "react";
import {
  Stack,
  Paper,
  Group,
  Text,
  TextInput,
  Title,
  Accordion,
  Box,
  Button,
  Table,
} from "@mantine/core";
import { HelpCircle, Building2, Plus, X } from "lucide-react";
import { showSuccess, showError } from "../../utils/notifications";
import {
  Partner,
  addManualPattern,
  unmapPattern,
} from "../../services/partnerService";

interface PatternSettingsPanelProps {
  customerId: string;
  partner: Partner;
  officialName: string;
  onNavigateToBasic: () => void;
  onUpdate: (silent?: boolean) => void;
}

/**
 * 名寄せパターン設定パネル
 */
export function PatternSettingsPanel({
  customerId,
  partner,
  officialName,
  onNavigateToBasic,
  onUpdate,
}: PatternSettingsPanelProps) {
  const [newPattern, setNewPattern] = useState("");
  const [addingPattern, setAddingPattern] = useState(false);

  // 名寄せパターンの手動追加
  const handleAddPattern = async () => {
    if (!newPattern.trim()) return;
    setAddingPattern(true);
    try {
      await addManualPattern(customerId, newPattern.trim(), partner.id);
      showSuccess("名寄せパターンを追加しました", "追加しました");
      setNewPattern("");
      onUpdate(true);
    } catch {
      showError("パターンの追加に失敗しました");
    } finally {
      setAddingPattern(false);
    }
  };

  // 名寄せパターンの解除
  const handleUnmapPattern = async (patternId: string, patternText: string) => {
    if (!confirm(`「${patternText}」の紐付けを解除しますか？`)) return;
    try {
      await unmapPattern(customerId, patternId);
      showSuccess("パターンの紐付けを解除しました", "解除しました");
      onUpdate(true);
    } catch {
      showError("紐付け解除に失敗しました");
    }
  };

  return (
    <Stack gap="md">
      <Title order={5}>名寄せ設定</Title>

      <Accordion variant="contained" radius="md" chevronPosition="left">
        <Accordion.Item value="hints">
          <Accordion.Control
            icon={
              <HelpCircle size={16} color="var(--mantine-color-blue-6)" />
            }
          >
            <Text size="sm" fw={700}>
              名寄せ（正規化）設定についてのヒントを表示
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Text size="xs" mb="sm">
              AI
              OCRが読み取った不確実な情報を、過去の紐付け履歴を「知識」として活用することで、
              <br />
              より確実に正しい取引先へ分類するための設定です。
            </Text>

            <Text size="xs" fw={700} mb={4}>
              本機能の役割
            </Text>
            <Box component="ul" style={{ margin: 0, paddingLeft: "1.2rem" }}>
              <Text component="li" size="xs">
                <strong>AIの推論を強化:</strong>
                <br />
                蓄積されたパターンと正式名称の関係性をAIに提示することで、表記の揺れや誤字があっても、
                <br />
                過去の事例から「最も可能性の高い取引先」をAIが精度高く特定できるようになります。
              </Text>
              <Text component="li" size="xs" mt={4}>
                <strong>精度の継続的向上:</strong>
                <br />
                紐付けデータ（正解例）が増えるほど、AIの判断材料が豊かになり、
                <br />
                システム全体の識別精度が段階的に向上していきます。
              </Text>
            </Box>

            <Text size="xs" fw={700} mt="sm" mb={4}>
              運用の流れ
            </Text>
            <Box component="ol" style={{ margin: 0, paddingLeft: "1.2rem" }}>
              <Text component="li" size="xs">
                <strong>パターンの収集:</strong>{" "}
                FAXやメールを処理するたびに、AIが抽出した「取引先の表記」が自動的に候補として蓄積されます。
              </Text>
              <Text component="li" size="xs" mt={4}>
                <strong>知識の提供（紐付け）:</strong>{" "}
                蓄積されたパターンを正式名称に紐付けることで、AIに「この書き方はこの会社のことだ」という正解を教えます。
              </Text>
            </Box>

            <Text size="xs" mt="sm">
              ※
              システム利用開始直後はリストは空ですが、処理が進むにつれてAIが自動的に候補を見つけ出します。
              <br />
              設定を行うことで、AIはより賢く、安定した分類を行えるようになります。
            </Text>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {/* 名寄せ管理パネル（シンプル＆ミニマル構成） */}
      <Paper withBorder radius="md" p="xl" shadow="xs">
        <Stack gap={40}>
          {/* 1. 集約先名称 */}
          <Stack gap="xs">
            <Text size="xs" fw={700} c="dimmed">
              名寄せ後の標準表記（集約先）
            </Text>
            <Group gap="sm" align="center">
              <Text size="xl" fw={800}>
                {officialName || "(名称未設定)"}
              </Text>
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={onNavigateToBasic}
                leftSection={<Building2 size={12} />}
              >
                名称を変更
              </Button>
            </Group>
            <Text size="xs" c="dimmed">
              ※
              以下の名寄せパターンが検出された際、この名称へ自動的に名寄せ（正規化）されます。
            </Text>
          </Stack>

          {/* 2. 新しいパターンの追加エリア */}
          <Stack gap="sm">
            <Text fw={700} size="sm">
              新しい名寄せパターンの追加
            </Text>
            <Group align="flex-end" wrap="nowrap">
              <TextInput
                placeholder="例: (株)三井酒造、ミツイシュゾウ、Mitsui"
                style={{ flex: 1 }}
                size="md"
                value={newPattern}
                onChange={(e) => setNewPattern(e.currentTarget.value)}
                onKeyDown={(e) => {
                  // IMEで変換中の場合は送信しない（Mac/Windowsの日本語入力対応）
                  if (e.nativeEvent.isComposing || e.keyCode === 229) {
                    return;
                  }
                  if (e.key === "Enter") handleAddPattern();
                }}
              />
              <Button
                size="md"
                onClick={handleAddPattern}
                loading={addingPattern}
                disabled={!newPattern.trim()}
                leftSection={<Plus size={18} />}
              >
                登録
              </Button>
            </Group>
            <Text size="xs" c="dimmed">
              AIがこれらを「知識」として学習するため、完全に一致していなくても名寄せの精度が向上します。
              <br />
              そのため、大量のパターンを網羅的に登録する必要はありません。
              <br />
              不足や精度の不安を感じた際に、都度追加することをおすすめします。
            </Text>
          </Stack>

          {/* 3. 登録済みのパターン一覧 */}
          <Stack gap="sm">
            <Text fw={700} size="sm">
              登録済みの名寄せパターン一覧
            </Text>

            {partner.patterns && partner.patterns.length > 0 ? (
              <Table verticalSpacing="md">
                <Table.Thead bg="var(--mantine-color-default-hover)">
                  <Table.Tr>
                    <Table.Th>名寄せパターン</Table.Th>
                    <Table.Th w={120}>出現回数</Table.Th>
                    <Table.Th w={80} ta="center">
                      操作
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {partner.patterns.map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td fw={500}>{p.pattern}</Table.Td>
                      <Table.Td>{p.occurrences}回</Table.Td>
                      <Table.Td ta="center">
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-xs"
                          leftSection={<X size={14} />}
                          onClick={() => handleUnmapPattern(p.id, p.pattern)}
                        >
                          解除
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text
                size="sm"
                c="dimmed"
                py="xl"
                ta="center"
                style={{
                  border: "1px dashed var(--mantine-color-default-border)",
                  borderRadius: "8px",
                }}
              >
                紐付いている名寄せパターンはありません
              </Text>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
